import React, { useEffect, useState } from "react";
import { Box, Grid, TextField, Typography, Autocomplete } from "@mui/material";
import apiEndpoints from "../../apiconfig";

const PartnerPreferences = ({ form, setField, isMobile, showErrors }) => {
  const [options, setOptions] = useState({
    bloodGroups: [],
    education: [],
    countries: [],
    states: [],
    cities: [],
    maritalStatuses: [],
    profession: [],
    religions: [],
    castes: [],
    zodiacSigns: [],
    zodiacStars: [],
    annualIncome: [],
  });

  const fetchFieldOptions = async (key, table, parentTable, parentId) => {
    try {
      let url = `${apiEndpoints.getOptions}?table=${table}`;
      if (parentTable && parentId)
        url += `&parent_table=${parentTable}&parent_id=${parentId}`;

      const res = await fetch(url);
      const data = await res.json();

      if (Array.isArray(data)) {
        setOptions((prev) => ({ ...prev, [key]: data }));
      } else {
        setOptions((prev) => ({ ...prev, [key]: [] }));
      }
    } catch (err) {
      console.error("Failed to fetch options for", table, err);
      setOptions((prev) => ({ ...prev, [key]: [] }));
    }
  };

  useEffect(() => {
    fetchFieldOptions("education", "education");
    fetchFieldOptions("profession", "profession");
    fetchFieldOptions("annualIncome", "annual_income");
    fetchFieldOptions("maritalStatuses", "marital_status");
    fetchFieldOptions("religions", "religion");
    fetchFieldOptions("castes", "caste");
    fetchFieldOptions("bloodGroups", "blood_group");
    fetchFieldOptions("zodiacSigns", "zodiac_moonsign");
    fetchFieldOptions("zodiacStars", "zodiac_star");
    fetchFieldOptions("countries", "country");
  }, []);

  useEffect(() => {
    if (form.pref_country_id) {
      fetchFieldOptions("states", "state", "country", form.pref_country_id);
    }
  }, [form.pref_country_id]);

  useEffect(() => {
    if (form.pref_state_id) {
      fetchFieldOptions("cities", "city", "state", form.pref_state_id);
    }
  }, [form.pref_state_id]);

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Partner Preferences
      </Typography>
      <Grid container spacing={2}>
        {/* Education */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.education}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.education.find((c) => c.name === form.pref_education) ||
              null
            }
            onChange={(_, v) => {
              if (v) {
                setField("pref_education", v.name);
                setField("pref_education_id", v.id);
              } else {
                setField("pref_education", "");
                setField("pref_education_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Education"
                fullWidth
                error={showErrors && !form.pref_education}
                helperText={
                  showErrors && !form.pref_education
                    ? "Education is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Profession */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.profession}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.profession.find((c) => c.name === form.pref_profession) ||
              null
            }
            onChange={(_, v) => {
              if (v) {
                setField("pref_profession", v.name);
                setField("pref_profession_id", v.id);
              } else {
                setField("pref_profession", "");
                setField("pref_profession_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Profession"
                fullWidth
                error={showErrors && !form.pref_profession}
                helperText={
                  showErrors && !form.pref_profession
                    ? "Profession is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Annual Income */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.annualIncome}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.annualIncome.find(
                (c) => c.name === form.pref_annual_income,
              ) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("pref_annual_income", v.name);
                setField("pref_annual_income_id", v.id);
              } else {
                setField("pref_annual_income", "");
                setField("pref_annual_income_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Annual Income"
                fullWidth
                error={showErrors && !form.pref_annual_income}
                helperText={
                  showErrors && !form.pref_annual_income
                    ? "Annual income is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Religion */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.religions}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.religions.find((c) => c.name === form.pref_religion) ||
              null
            }
            onChange={(_, v) => {
              if (v) {
                setField("pref_religion", v.name);
                setField("pref_religion_id", v.id);
              } else {
                setField("pref_religion", "");
                setField("pref_religion_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Religion"
                fullWidth
                error={showErrors && !form.pref_religion}
                helperText={
                  showErrors && !form.pref_religion
                    ? "Religion is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Marital Status */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.maritalStatuses}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.maritalStatuses.find(
                (c) => c.name === form.pref_marital_status,
              ) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("pref_marital_status", v.name);
                setField("pref_marital_status_id", v.id);
              } else {
                setField("pref_marital_status", "");
                setField("pref_marital_status_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Marital Status"
                fullWidth
                error={showErrors && !form.pref_marital_status}
                helperText={
                  showErrors && !form.pref_marital_status
                    ? "Marital status is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Caste */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.castes}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.castes.find((c) => c.name === form.pref_caste) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("pref_caste", v.name);
                setField("pref_caste_id", v.id);
              } else {
                setField("pref_caste", "");
                setField("pref_caste_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Caste"
                fullWidth
                error={
                  showErrors &&
                  !form.pref_religion?.toLowerCase().includes("muslim") &&
                  !form.pref_caste
                }
                helperText={
                  showErrors &&
                  !form.pref_religion?.toLowerCase().includes("muslim") &&
                  !form.pref_caste
                    ? "Caste is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Blood Group */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.bloodGroups}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.bloodGroups.find(
                (c) => c.name === form.pref_blood_group,
              ) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("pref_blood_group", v.name);
                setField("pref_blood_group_id", v.id);
              } else {
                setField("pref_blood_group", "");
                setField("pref_blood_group_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Blood Group"
                fullWidth
                error={showErrors && !form.pref_blood_group}
                helperText={
                  showErrors && !form.pref_blood_group
                    ? "Blood group is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Zodiac Sign */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.zodiacSigns}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.zodiacSigns.find(
                (c) => c.name === form.pref_zodiac_sign,
              ) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("pref_zodiac_sign", v.name);
                setField("pref_zodiac_sign_id", v.id);
              } else {
                setField("pref_zodiac_sign", "");
                setField("pref_zodiac_sign_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Zodiac Sign"
                fullWidth
                error={
                  showErrors &&
                  !form.pref_religion?.toLowerCase().includes("muslim") &&
                  !form.pref_zodiac_sign
                }
                helperText={
                  showErrors &&
                  !form.pref_religion?.toLowerCase().includes("muslim") &&
                  !form.pref_zodiac_sign
                    ? "Zodiac sign is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Zodiac Star */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.zodiacStars}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.zodiacStars.find(
                (c) => c.name === form.pref_zodiac_star,
              ) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("pref_zodiac_star", v.name);
                setField("pref_zodiac_star_id", v.id);
              } else {
                setField("pref_zodiac_star", "");
                setField("pref_zodiac_star_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Zodiac Star"
                fullWidth
                error={
                  showErrors &&
                  !form.pref_religion?.toLowerCase().includes("muslim") &&
                  !form.pref_zodiac_star
                }
                helperText={
                  showErrors &&
                  !form.pref_religion?.toLowerCase().includes("muslim") &&
                  !form.pref_zodiac_star
                    ? "Zodiac star is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* Country */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.countries}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.countries.find((c) => c.name === form.pref_country) ||
              null
            }
            onChange={(_, v) => {
              if (v) {
                setField("pref_country", v.name);
                setField("pref_country_id", v.id);
                setField("pref_state", "");
                setField("pref_state_id", null);
                setField("pref_city", "");
                setField("pref_city_id", null);
              } else {
                setField("pref_country", "");
                setField("pref_country_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Country"
                fullWidth
                error={showErrors && !form.pref_country_id}
                helperText={
                  showErrors && !form.pref_country_id
                    ? "Country is required"
                    : ""
                }
              />
            )}
          />
        </Grid>

        {/* State */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.states}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.states.find((c) => c.name === form.pref_state) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("pref_state", v.name);
                setField("pref_state_id", v.id);
                setField("pref_city", "");
                setField("pref_city_id", null);
              } else {
                setField("pref_state", "");
                setField("pref_state_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="State"
                fullWidth
                error={showErrors && !form.pref_state_id}
                helperText={
                  showErrors && !form.pref_state_id ? "State is required" : ""
                }
              />
            )}
            noOptionsText={
              !form.pref_country_id
                ? "Please select a country first"
                : options.states.length === 0
                  ? "No states found"
                  : ""
            }
          />
        </Grid>

        {/* City */}
        <Grid item xs={12} sm={6} width={isMobile ? "100%" : "48%"}>
          <Autocomplete
            options={options.cities}
            getOptionLabel={(option) => option?.name || ""}
            value={
              options.cities.find((c) => c.name === form.pref_city) || null
            }
            onChange={(_, v) => {
              if (v) {
                setField("pref_city", v.name);
                setField("pref_city_id", v.id);
              } else {
                setField("pref_city", "");
                setField("pref_city_id", null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="City"
                fullWidth
                error={showErrors && !form.pref_city_id}
                helperText={
                  showErrors && !form.pref_city_id ? "City is required" : ""
                }
              />
            )}
            noOptionsText={
              !form.pref_state_id
                ? "Please select a state first"
                : options.cities.length === 0
                  ? "No cities found"
                  : ""
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PartnerPreferences;
